import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { getScheduleById } from '../../api/schedule';
import { useBreakdownTabContext } from '../../contexts/BreakdownTabContext';
import { useCurrentUser } from '../../contexts/CurrentUserContext';
import Schedule from '../Schedule/Schedule';

/** `#/schedule/:scheduleID`：同步分场上下文后渲染拍摄计划页。 */
const ScheduleRoutePage: React.FC = () => {
  const { scheduleID } = useParams<{ scheduleID: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setBreakdownId, switchTab, breakdownId } = useBreakdownTabContext();
  const { stripServerSyncedUserLastUsedScheduleByScheduleId } = useCurrentUser();
  const [hydrated, setHydrated] = useState(false);
  const [hydrateError, setHydrateError] = useState<string | null>(null);

  useLayoutEffect(() => {
    switchTab('schedule');
  }, [switchTab]);

  useEffect(() => {
    if (!scheduleID) {
      setHydrated(true);
      return;
    }
    let cancelled = false;
    (async () => {
      let treatAsMissingSchedule = false;
      try {
        const res = await getScheduleById(scheduleID);
        const bid = res.data?.scheduleBelongBreakdownID;
        if (!cancelled && bid) {
          setBreakdownId(bid);
        }
      } catch (e) {
        if (!cancelled && axios.isAxiosError(e) && e.response?.status === 404 && scheduleID) {
          treatAsMissingSchedule = true;
          stripServerSyncedUserLastUsedScheduleByScheduleId(scheduleID);
          navigate(
            {
              pathname: '/schedule',
              search: new URLSearchParams({ tab: 'details' }).toString(),
            },
            {
              replace: true,
              state: {
                noScheduleSelected: true,
                ...(breakdownId ? { breakdownID: breakdownId } : {}),
              },
            },
          );
        } else if (!cancelled) {
          setHydrateError(e instanceof Error ? e.message : String(e));
        }
      } finally {
        if (!cancelled && !treatAsMissingSchedule) {
          setHydrated(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [scheduleID, setBreakdownId, navigate, breakdownId, stripServerSyncedUserLastUsedScheduleByScheduleId]);

  if (!hydrated) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 60,
          color: 'var(--color-gray2, #888)',
        }}
      >
        <span>{t('common.loading')}</span>
      </div>
    );
  }

  if (hydrateError) {
    return <div style={{ padding: 24, color: 'var(--error-color, #c00)' }}>{hydrateError}</div>;
  }

  if (!scheduleID) {
    return <Schedule />;
  }

  return <Schedule scheduleID={scheduleID} />;
};

export default ScheduleRoutePage;
